import React, { useState } from "react";
import WarningModal from "src/components/shared-components/modal/WarningModal";
import PackageActionModal from "./modals/PackageActionModal";
import { requestService } from "src/api/request/requestService";
import type { PackageAction } from "src/api/request/requestService";
import { useSnackbar } from "notistack";
import { Box } from "@mui/material";
import { REQUEST_LIST_GRID_COMPACT_SX } from "../constants";
import { GridColDef } from "@mui/x-data-grid-premium";
import { dateTimeFormatter } from "src/utils/date";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import { useTranslate } from "src/locales";
import type { CompletedRequestRow } from "../types";

interface CompletedRequestsGridProps {
  rows: CompletedRequestRow[];
  loading?: boolean;
  onRowSelect?: (row: CompletedRequestRow) => void;
  selectedRequestId?: number | string | null;
  hasCancelPrivilege?: boolean;
  currentUser?: string;
  ptId?: string | number;
  onCancel?: (row: CompletedRequestRow) => void;
  onCancelApproved?: (row: CompletedRequestRow) => void;
}

export default function CompletedRequestsGrid({
  rows,
  loading = false,
  onRowSelect,
  selectedRequestId,
  hasCancelPrivilege = false,
  currentUser = "",
  ptId,
  onCancel,
  onCancelApproved,
}: CompletedRequestsGridProps) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  // Pending cancel warning modal state
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [rowToCancel, setRowToCancel] = useState<CompletedRequestRow | null>(null);

  // Approved cancel warning modal state
  const [approvedWarningOpen, setApprovedWarningOpen] = useState(false);
  const [rowToCancelApproved, setRowToCancelApproved] = useState<CompletedRequestRow | null>(null);

  // Package action modal state
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [pendingCancelNote, setPendingCancelNote] = useState("");

  // --- Pending cancel flow (existing) ---
  const handleCancelClick = (row: CompletedRequestRow) => {
    setRowToCancel(row);
    setWarningModalOpen(true);
  };

  const handleWarningModalClose = () => {
    setWarningModalOpen(false);
    setRowToCancel(null);
  };

  const handleConfirmCancel = async (note: string) => {
    if (!rowToCancel) return;

    try {
      const response = await requestService.cancelRequest({
        requestId: Number(rowToCancel.id),
        cancelNote: note,
        cancelUser: currentUser,
      });

      if (response.issuccess) {
        enqueueSnackbar(t("common.success.cancel", "default", { defaultValue: "İşlem başarıyla iptal edildi." }), { variant: "success" });
        onCancel?.(rowToCancel); 
        handleWarningModalClose();
      } else {
        enqueueSnackbar(response.message || t("common.error.general", "default", { defaultValue: "Bir hata oluştu." }), { variant: "error" });
      }
    } catch (error) {
       console.error("Cancel request error:", error);
       enqueueSnackbar(t("common.error.general", "default", { defaultValue: "Bir hata oluştu." }), { variant: "error" });
    }
  };

  // --- Approved cancel flow (new) ---
  const handleCancelApprovedClick = (row: CompletedRequestRow) => {
    setRowToCancelApproved(row);
    setApprovedWarningOpen(true);
  };

  const handleApprovedWarningClose = () => {
    setApprovedWarningOpen(false);
    setRowToCancelApproved(null);
    setPendingCancelNote("");
  };

  const callCancelApprovedApi = async (
    row: CompletedRequestRow,
    note: string,
    packageAction?: PackageAction | null
  ) => {
    try {
      const response = await requestService.cancelApprovedRequest({
        requestId: Number(row.id),
        ptId: Number(ptId),
        cancelNote: note,
        packageAction: packageAction || null,
      });

      if (response.issuccess) {
        enqueueSnackbar(
          t("common.success.cancel", "default", { defaultValue: "İşlem başarıyla iptal edildi." }),
          { variant: "success" }
        );
        onCancelApproved?.(row);
        handleApprovedWarningClose();
        setPackageModalOpen(false);
      } else {
        enqueueSnackbar(
          response.message || t("common.error.general", "default", { defaultValue: "Bir hata oluştu." }),
          { variant: "error" }
        );
      }
    } catch (error: any) {
      const errorCode = error?.response?.data?.errorCode || error?.response?.data?.ErrorCode;
      const errorMessage = error?.response?.data?.message || error?.response?.data?.Message;

      if (errorCode === "DELETE_PROCESS_025") {
        // Paket seçimi gerekli
        setPendingCancelNote(note);
        setApprovedWarningOpen(false);
        setPackageModalOpen(true);
      } else {
        console.error("Cancel approved request error:", error);
        enqueueSnackbar(
          errorMessage || t("common.error.general", "default", { defaultValue: "Bir hata oluştu." }),
          { variant: "error" }
        );
      }
    }
  };

  const handleConfirmCancelApproved = async (note: string) => {
    if (!rowToCancelApproved) return;
    await callCancelApprovedApi(rowToCancelApproved, note);
  };

  const handlePackageActionSelect = async (action: PackageAction) => {
    if (action === "Cancel") {
      setPackageModalOpen(false);
      return;
    }
    if (!rowToCancelApproved) return;
    await callCancelApprovedApi(rowToCancelApproved, pendingCancelNote, action);
  };

  // --- Enabling rules ---
  const isCancelEnabled = (row: CompletedRequestRow): boolean => {
    if (row.status !== "Beklemede") return false;
    return row.cuser === currentUser || hasCancelPrivilege;
  };

  const isCancelApprovedEnabled = (row: CompletedRequestRow): boolean => {
    return row.status === "Onaylandı";
  };

  const columns: GridColDef<CompletedRequestRow>[] = [
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const cancelEnabled = isCancelEnabled(params.row);
        const cancelApprovedEnabled = isCancelApprovedEnabled(params.row);
        
        return (
          <ByzMuiOpenProcessMenu
            hideHeaderTitle
            menuItems={[
              {
                label: t("request-list.menu.cancel", "default", { defaultValue: "İptal" }),
                onClick: () => handleCancelClick(params.row),
                disabled: !cancelEnabled,
              },
              {
                label: t("request-list.menu.cancel-approved", "default", { defaultValue: "İptal(Onaylanmışlar)" }),
                onClick: () => handleCancelApprovedClick(params.row),
                disabled: !cancelApprovedEnabled,
                divider: true,
              },
            ]}
            onClose={() => {}}
          />
        );
      },
    },
    {
      field: "performedBy",
      headerName: t("request-list.columns.performed-by", "default", { defaultValue: "İşlem Yapan" }),
      flex: 1,
      minWidth: 120,
    },
    {
      field: "actionDate",
      valueFormatter: (value) => dateTimeFormatter(value, "dd.MM.yyyy HH:mm:ss"),
      headerName: t("request-list.columns.action-date", "default", { defaultValue: "İşlem Tarihi" }),
      width: 150,
    },
    {
      field: "requestType",
      headerName: t("request-list.columns.request-type", "default", { defaultValue: "İstek Türü" }),
      flex: 1,
      minWidth: 180,
    },
    {
      field: "departmentName",
      headerName: t("request-list.columns.department-name", "default", { defaultValue: "Bölüm Adı" }),
      flex: 1,
      minWidth: 150,
    },
    {
      field: "doctorName",
      headerName: t("request-list.columns.doctor-name", "default", { defaultValue: "Doktor Adı" }),
      flex: 1,
      minWidth: 140,
    },
    {
      field: "status",
      headerName: t("request-list.columns.status", "default", { defaultValue: "Durumu" }),
      width: 120,
    },
    {
      field: "requestDate",
      headerName: t("request-list.columns.request-date", "default", { defaultValue: "İstek Tarihi" }),
      flex: 1,
      minWidth: 160,
      valueFormatter: (value) => dateTimeFormatter(value, "dd.MM.yyyy HH:mm:ss"),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <ByzMuiDataGrid
        rows={rows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        pagination
        pageSizeOptions={[10]}
        initialState={{ 
          density: "compact",
          pagination: { paginationModel: { pageSize: 10 } }
        }}
        loading={loading}
        autoHeight
        customToolbar={() => (
          <ByzDataGridToolbar
            searchPlaceholder={t("request-list.search", "default", { defaultValue: "Ara..." })}
            searchIconColor="#1E88E5"
          />
        )}
        whiteGrid
        headerWhite
        getRowId={(row) => row.id}
        onRowClick={(params) => onRowSelect?.(params.row)}
        sx={{
          ...REQUEST_LIST_GRID_COMPACT_SX,
          "& .MuiDataGrid-row": {
            cursor: onRowSelect ? "pointer" : "default",
            "&.Mui-selected": {
              bgcolor: "action.selected",
            },
          },
        }}
      />

      {/* Pending cancel warning modal */}
      <WarningModal
        open={warningModalOpen}
        onClose={handleWarningModalClose}
        onConfirm={handleConfirmCancel}
        message={t("request-list.warning.cancel-confirm", "default", { defaultValue: "Seçili istek iptal edilecek, emin misiniz?" })}
        hasReadyMadeText={false}
      />

      {/* Approved cancel warning modal */}
      <WarningModal
        open={approvedWarningOpen}
        onClose={handleApprovedWarningClose}
        onConfirm={handleConfirmCancelApproved}
        message={t("request-list.warning.cancel-approved-confirm", "default", { defaultValue: "Onaylanmış istek iptal edilecek, emin misiniz?" })}
        hasReadyMadeText={false}
      />

      {/* Package action selection modal (DELETE_PROCESS_025) */}
      <PackageActionModal
        open={packageModalOpen}
        onClose={() => setPackageModalOpen(false)}
        onSelect={handlePackageActionSelect}
      />
    </Box>
  );
}
